import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Save, Upload, ImageIcon } from "lucide-react";
import { adminAxios, absoluteUrl, formatApiError } from "../../lib/adminApi";
import { Input } from "../../components/ui/input";

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await adminAxios.get("/admin/gallery");
      setItems(data.images || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });

    const newItems = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const fd = new FormData();
        fd.append("file", files[i]);
        const up = await adminAxios.post("/admin/upload/gallery", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const created = await adminAxios.post("/admin/gallery", {
          title: "",
          location: "",
          url: up.data.url,
        });
        newItems.unshift(created.data);
      } catch (err) {
        toast.error(`${files[i].name}: ${formatApiError(err)}`);
      }
      setProgress({ done: i + 1, total: files.length });
    }
    if (newItems.length) {
      toast.success(`${newItems.length} photo${newItems.length > 1 ? "s" : ""} uploaded`);
      await load();
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFilePick = (e) => handleFiles(e.target.files);

  // Drag & drop
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    handleFiles(e.dataTransfer.files);
  };

  const updateItem = async (id, patch) => {
    try {
      await adminAxios.put(`/admin/gallery/${id}`, patch);
      toast.success("Saved");
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await adminAxios.delete(`/admin/gallery/${id}`);
      toast.success("Deleted");
      setItems((arr) => arr.filter((it) => it.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-gallery-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Gallery</h1>
        <p className="text-slate-500 text-sm mt-1">
          Drop or pick installation photos — we auto-rotate, resize and optimise them. Title and location are optional and editable later.
        </p>
      </div>

      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={() => !uploading && fileRef.current?.click()}
        data-testid="gallery-upload-zone"
        className={`relative border-2 border-dashed rounded-2xl p-10 md:p-12 text-center transition-colors cursor-pointer ${
          uploading ? "border-[#1C6B8A] bg-[#E8F4F8]" : "border-slate-200 bg-white hover:border-[#1C6B8A] hover:bg-slate-50"
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onFilePick}
          data-testid="gallery-file-input"
          className="hidden"
        />
        <div className="w-14 h-14 rounded-full bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center mx-auto mb-4">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
        </div>
        <div className="font-display font-semibold text-slate-900 text-lg">
          {uploading
            ? `Uploading ${progress.done} of ${progress.total}...`
            : "Drag & drop photos here, or click to choose"}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          JPG, PNG, WebP, HEIC up to 25 MB each · multiple files supported · auto-resized to 1600 px max
        </p>
        {uploading && progress.total > 0 && (
          <div className="mt-5 max-w-sm mx-auto h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1C6B8A] transition-all"
              style={{ width: `${(progress.done / progress.total) * 100}%` }}
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#1C6B8A]" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-500">
          <ImageIcon className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          No photos yet. Upload your first installation above.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-list">
          {items.map((it) => (
            <GalleryCard key={it.id} item={it} onSave={updateItem} onDelete={deleteItem} />
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryCard({ item, onSave, onDelete }) {
  const [edit, setEdit] = useState({ title: item.title || "", location: item.location || "" });
  const changed = edit.title !== (item.title || "") || edit.location !== (item.location || "");
  return (
    <div
      className="bg-white rounded-2xl border border-slate-100 overflow-hidden card-soft"
      data-testid={`gallery-card-${item.id}`}
    >
      <div className="relative aspect-[4/3] bg-slate-900 overflow-hidden">
        <img
          src={absoluteUrl(item.url)}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60"
        />
        <img
          src={absoluteUrl(item.url)}
          alt={item.title || "Installation"}
          className="relative w-full h-full object-contain"
        />
      </div>
      <div className="p-4 space-y-2">
        <Input
          value={edit.title}
          onChange={(e) => setEdit({ ...edit, title: e.target.value })}
          placeholder="Title (optional)"
          className="text-sm"
          data-testid={`gallery-edit-title-${item.id}`}
        />
        <Input
          value={edit.location}
          onChange={(e) => setEdit({ ...edit, location: e.target.value })}
          placeholder="Location (optional)"
          className="text-sm"
          data-testid={`gallery-edit-location-${item.id}`}
        />
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => onSave(item.id, edit)}
            disabled={!changed}
            data-testid={`gallery-save-${item.id}`}
            className="flex-1 h-9 rounded-full brand-btn text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          <button
            onClick={() => onDelete(item.id)}
            data-testid={`gallery-delete-${item.id}`}
            className="h-9 w-9 rounded-full border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
